import { FilterTypeModel } from './abstract-filter-type.model';
import { FilterTypesEnum } from '../enums/filters-types.enum';
import { ChipModel } from '../../Forms/models/chip.model';
import { FilterQuickOptionModel } from './filter-quick-option.model';
import { IncludeExcludeEnum } from '../enums/include-exclude.enum';

export class FilterTypeTextModel extends FilterTypeModel<string[]> {
    readonly key: FilterTypesEnum = FilterTypesEnum.text;
    protected value: string[];
    protected currentValue: string[];

    showMagnifyingGlass: boolean = true;
    showAddCustomButton: boolean = false;
    placeholder: string;
    prefix: string;

    constructor(
        id: string,
        label: string,
        value?: string[],
        placeholder?: string,
        showMagnifyingGlass: boolean = true,
        showAddCustomButton: boolean = false,
        prefix?: string,
        section?: string,
        useExactQuickSearchString?: boolean,
        showIncludeExclude?: boolean
    ) {
        super(id, label, section, showIncludeExclude);
        this.placeholder = placeholder;
        this.showMagnifyingGlass = showMagnifyingGlass;
        this.setValue(value || []);
        this.showAddCustomButton = showAddCustomButton;
        this.prefix = prefix;
        this.useExactQuickSearchString = useExactQuickSearchString;
    }

    /**
     * Sets the active value for a filter
     * @param value
     */
    setValue(value: string[]): void {
        this.value = value;
        this.chips = this.value.map((val) => {
            return {label: val};
        });
        this.cancelChanges();
        this.updateChipSuffix();
    }

    /**
     * Gets the active value for a filter
     */
    getValue(): string[] {
        return this.value;
    }

    /**
     * Handles deleting a chip for the active value of this filter
     * @param chip A chip from this filter
     */
    removeChip(chip: ChipModel): void {
        this.setValue(this.value.filter(val => val !== chip.label));
    }

    /**
     * Resets the filter to not apply any changes
     */
    clear(): void {
        this.setValue([]);
        this.setIncludeExclude(IncludeExcludeEnum.include);
    }

    /**
     * Checks if an item can be added to the value
     * @param item An item to add to the value
     */
    canAddItem(item: string): boolean {
        if (!item || item.trim().length < 1) {
            return false;
        }
        const exists = this.value.includes(item);

        // For handling non-case sensitive options
        // const exists = !!this.value.find((val) => {
        //     return val.toLocaleLowerCase() === item.toLocaleLowerCase();
        // });

        return !exists;
    }

    /**
     * Sets the current value for a filter
     * @param value
     */
    setCurrentValue(value: string[]): void {
        this.currentValue = value;
        this.currentChips = this.currentValue.map((val) => {
            return {label: val};
        });

        this.updateCurrentChipSuffix();
    }

    /**
     * Gets the current value for a filter
     */
    getCurrentValue(): string[] {
        return this.currentValue;
    }

    /**
     * Handles deleting a chip for the current value of this filter
     * @param chip A chip from this filter
     */
    removeCurrentChip(chip: ChipModel): void {
        this.setCurrentValue(this.currentValue.filter(val => val !== chip.label));
    }

    /**
     * Checks if an item can be added to the value
     * @param item An item to add to the value
     */
    canAddCurrentItem(item: string): boolean {
        if (!item || item.trim().length < 1) {
            return false;
        }
        const exists = this.currentValue.includes(item);

        // For handling non-case sensitive options
        // const exists = !!this.value.find((val) => {
        //     return val.toLocaleLowerCase() === item.toLocaleLowerCase();
        // });

        return !exists;
    }

    /**
     * Resets the current value to the active value
     */
    cancelChanges(): void {
        this.setCurrentValue(this.value?.slice());
    }

    quickAutocomplete(query: string, translateMethod: (str: string) => string): FilterQuickOptionModel<string>[] {
        if (!this.canAddCurrentItem(query)) {
            return [];
        }

        return [{
            label: query,
            value: query,
            filter: this
        }];
    }
    addQuickOption(option: FilterQuickOptionModel<string>): void {
        this.setValue([...this.value, option.value]);
    }
}
